import {
  ConfirmationDialogBox,
  IconButton,
  NumericInput,
  Table,
  TableBody,
  TableCell,
  TableFoot as TableFooter,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  CurrencyUsdIcon,
  DeleteIcon,
  PlusCircleOutlineIcon,
} from '@atgfr8/icons';
import { Notification, TableRowErrors, ToastType } from '@components';
import { EmptyData } from '@components/EmptyData/EmptyData';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import {
  orderStore,
  updateVendorPropertyAtIndex,
  validateFieldForOrder,
  vendorEFSCheckRequest,
} from '@store/orders';
import {
  LineItemViewModel,
  VendorLineItem,
} from '@typeDefinitions/lineItemTypes';
import { VendorContractViewModel } from '@typeDefinitions/vendorTypes';
import {
  formatAmount,
  handleToast,
  isAdmin,
  isLTLAdmin,
  isSuperAdmin,
  orderTableErrors,
  printLog,
} from '@utils/utils';
import { DELETE_VENDOR_PAYMENTS_LINE_ITEM_DIALOG_ID } from '@views/order/constants';
import { debounce, get, size } from 'lodash';
import { Accessor, Index, Show, createMemo, createSignal } from 'solid-js';

import { removeTableRow } from '../stops/stopsEditor/utilFunctions';
import classes from './classes';

type Props = {
  vendor: VendorContractViewModel;
  index: number;
};

export const VendorPay = (props: Props) => {
  const [deleteRow, setDeleteRow] = createSignal<number | null>(null);

  const geLineItems = () => get(props.vendor, 'lineItems', []);

  const addRow = () => {
    const lineItems = props.vendor.lineItems ? [...props.vendor.lineItems] : [];
    const insertObj: LineItemViewModel = {
      operationType: 'Insert',
      type: 'Accessorial',
      quantity: 1,
      description: '',
      rate: 0,
      id: 0,
      estimate: false,
      readOnly: false,
      synched: false,
      adjustment: isFalveyInsurance(),
      stopId: 0,
      pickUpId: undefined,
      dropOffId: undefined,
      descriptionWithParanthesis: '',
    };
    lineItems.push(insertObj as unknown as VendorLineItem);
    updateVendorPropertyAtIndex({ lineItems: lineItems });
  };

  const headers = () => {
    const columnHeaders = [
      'Description',
      'Qty',
      'Rate',
      'EFS Request',
      'Total',
    ];
    if (isFalveyInsurance()) {
      columnHeaders.splice(3, 1);
    }
    return columnHeaders;
  };

  const cellStyle = {
    padding: '5px 8px',
    'border-bottom': '0px',
    'min-width': '140px',
    'max-width': '140px',
  };

  const getRowBorderCellClass = (index: number) =>
    index === 0 ? '' : 'border-t! border-[#a8c5bf]!';

  const computeTotalLineItems = () => {
    return size(geLineItems().filter((x) => x.operationType !== 'Delete')) > 0
      ? geLineItems()
          .filter((x) => x.operationType !== 'Delete')
          .reduce(
            (acc, item) => acc + Number(item.rate) * Number(item.quantity),
            0,
          )
      : 0;
  };

  const onLineItemsChange = (
    key: string,
    value: string | number | boolean,
    index: number,
  ) => {
    let lineItems = props.vendor.lineItems ? [...props.vendor.lineItems] : [];
    lineItems = lineItems.filter((item) => item.operationType !== 'Delete');
    if (lineItems[index].operationType == 'Insert') {
      lineItems[index] = { ...lineItems[index], [key]: value };
    } else {
      lineItems[index] = {
        ...lineItems[index],
        [key]: value,
        operationType: 'Update',
      };
    }
    updateVendorPropertyAtIndex({ lineItems: lineItems });
  };

  const debouncedApiCall = debounce(async (itemId, description, rate) => {
    try {
      await vendorEFSCheckRequest({
        lineItemId: itemId,
        comment: description,
        amount: rate,
      });
    } catch (error) {
      handleToast(ToastType.Error, (error as Error).message);
    }
  }, 500);

  const getEfsRequestStyling = (lineItemId: number) => {
    const lineItem: VendorLineItem | undefined = orderStore.order.vendors?.[
      orderStore.activeTab.index
    ]?.lineItems?.find((obj: VendorLineItem) => obj.id === lineItemId);

    const statusClassMap = {
      'Check Approved': classes.successColor,
      Requested: classes.successColor,
      Unsettled: classes.dollarIcon,
      'Pending Sync': classes.dollarIcon,
    };

    let classString =
      (statusClassMap[
        lineItem?.status as keyof typeof statusClassMap
      ] as string) || classes.dollarIcon;
    if (lineItem?.readOnly) {
      classString += ' cursor-not-allowed';
    }

    return classString;
  };

  const userHasEFSPermission = () => {
    return isAdmin() || isSuperAdmin();
  };

  const handleEFSClick = async (
    lineItemId: number,
    item: LineItemViewModel,
  ) => {
    const lineItem: VendorLineItem | undefined = orderStore.order.vendors?.[
      orderStore.activeTab.index
    ]?.lineItems?.find((obj: VendorLineItem) => obj.id === lineItemId);
    if (lineItem === undefined) {
      return false;
    }
    switch (lineItem.status) {
      case 'Check Approved':
      case 'Requested':
        handleToast(
          ToastType.Caution,
          'Check has already been requested or approved.',
        );
        return false;
      case 'Unsettled':
        try {
          await debouncedApiCall(item.id, item.description, item.rate);
        } catch (error) {
          printLog('Failed to make EFS check request:', error);
        }
        break;
    }

    if (props.vendor.lineItems || [].length > 0) {
      const updatedLineItems = props.vendor.lineItems?.map((currentItem) =>
        currentItem.id === lineItemId
          ? { ...currentItem, status: 'Check Approved' }
          : currentItem,
      );
      updateVendorPropertyAtIndex({ lineItems: updatedLineItems });
    }
  };

  const isFalveyInsurance = () => {
    return props.vendor.name === 'Falvey Shippers Insurance';
  };

  const isLoadSureInsurance = (ind: number) => {
    if (ind === 0) {
      return props.vendor.name === 'Reliance Partners, LLC';
    }
  };

  return (
    <>
      <div>
        <Notification
          type={'info'}
          text={
            'To Request an EFS Check to pay a Vendor, please email carriers@armstrongtransport.com'
          }
        />
        <Table>
          <TableHead class="head">
            <TableRow>
              {headers().map((item) => (
                <TableHeaderCell class="text-black font-medium text-[12px]">
                  {item}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <Show
            when={
              size(
                geLineItems().filter((x) => x.operationType !== 'Delete'),
              ) === 0
            }
          >
            <TableBody class="w-full h-64 bg-[#BFDFD8]">
              <TableRow>
                <TableCell colSpan={headers().length + 1} style={{ border: 0 }}>
                  <EmptyData />
                </TableCell>
              </TableRow>
            </TableBody>
          </Show>
          <Show
            when={
              size(geLineItems().filter((x) => x.operationType !== 'Delete')) >
              0
            }
          >
            <TableBody class={classes.tableBg}>
              <Index each={geLineItems()}>
                {(item: Accessor<VendorLineItem>, index) => {
                  const isUnqualifiedEFSCheckRequest = createMemo(() => {
                    return ![
                      'Check Approved',
                      'Requested',
                      'Unsettled',
                    ].includes(item().status);
                  });

                  return (
                    <Show when={item().operationType !== 'Delete'}>
                      <TableRow class={classes.tableRow}>
                        <TableCell
                          class={getRowBorderCellClass(index)}
                          style={{
                            ...cellStyle,
                            'max-width': 'auto',
                            'min-width': '35%',
                          }}
                        >
                          <TextField
                            class={'bg-white rounded-sm h-[40px]!'}
                            value={item().description}
                            onChange={(value: string) => {
                              onLineItemsChange('description', value, index);
                            }}
                            label=""
                            placeholder="Description"
                            size="sm"
                            disabled={
                              (isFalveyInsurance() &&
                                !isLTLAdmin() &&
                                !(isSuperAdmin() && item().adjustment)) ||
                              isLoadSureInsurance(index)
                            }
                            dataTestId={`description-textInput-${index}`}
                          />
                        </TableCell>
                        <TableCell
                          class={getRowBorderCellClass(index)}
                          style={{
                            ...cellStyle,
                            'min-width': '70px',
                            'max-width': '70px',
                          }}
                        >
                          <NumericInput
                            label=""
                            class={'bg-white rounded-sm h-[40px]!'}
                            value={item().quantity}
                            onChange={(
                              value: string | number | null | Event,
                            ) => {
                              onLineItemsChange(
                                'quantity',
                                value as unknown as number,
                                index,
                              );
                            }}
                            placeholder="Qty"
                            on:blur={() =>
                              validateFieldForOrder(
                                `vendors.${props.index}.lineItems.${index}.quantity`,
                                `vendors[${props.index}].lineItems[${index}].quantity`,
                              )
                            }
                            error={Boolean(
                              orderStore.orderFormError &&
                              orderStore.orderFormError[
                                `vendors.${props.index}.lineItems.${index}.quantity`
                              ],
                            )}
                            disabled={
                              (isFalveyInsurance() &&
                                !isLTLAdmin() &&
                                !(isSuperAdmin() && item().adjustment)) ||
                              isLoadSureInsurance(index)
                            }
                            size="sm"
                            dataTestId={`quantity-textInput-${index}`}
                          />
                        </TableCell>
                        <TableCell
                          class={getRowBorderCellClass(index)}
                          style={cellStyle}
                        >
                          <NumericInput
                            class={'bg-white rounded-sm h-[40px]!'}
                            value={item().rate}
                            onChange={(value: string) => {
                              onLineItemsChange('rate', value, index);
                            }}
                            label=""
                            placeholder="Rate"
                            on:blur={() =>
                              validateFieldForOrder(
                                `vendors.${props.index}.lineItems.${index}.rate`,
                                `vendors[${props.index}].lineItems[${index}].rate`,
                              )
                            }
                            error={Boolean(
                              orderStore.orderFormError &&
                              orderStore.orderFormError[
                                `vendors.${props.index}.lineItems.${index}.rate`
                              ],
                            )}
                            disabled={
                              (isFalveyInsurance() &&
                                !isLTLAdmin() &&
                                !(isSuperAdmin() && item().adjustment)) ||
                              isLoadSureInsurance(index)
                            }
                            size="sm"
                            dataTestId={`rate-textInput-${index}`}
                          />
                        </TableCell>
                        <Show when={!isFalveyInsurance()}>
                          <TableCell
                            class={`text-center! ${getRowBorderCellClass(index)}`}
                            style={{ width: '120px' }}
                          >
                            <IconButton
                              onClick={() =>
                                handleEFSClick(
                                  item().id,
                                  item() as unknown as LineItemViewModel,
                                )
                              }
                              disabled={
                                item().id === 0 ||
                                !userHasEFSPermission() ||
                                isFalveyInsurance() ||
                                isLoadSureInsurance(index) ||
                                isUnqualifiedEFSCheckRequest()
                              }
                              class="[&:disabled_svg]:bg-[#a2a2a2]! [&:disabled]:cursor-not-allowed! [&:disabled_svg]:cursor-not-allowed!"
                              style={{ 'pointer-events': 'all' }}
                              title={
                                // ideally, the status will not go to requested on the vendors tab
                                // only admins can request checks on the vendor side, so they would always be auto-approved
                                // but if this ever changed, this code will not need to be updated
                                // assuming the statuses will not change
                                item().status === 'Check Approved'
                                  ? 'Check Approved'
                                  : item().status === 'Requested'
                                    ? 'EFS Check requested'
                                    : ''
                              }
                              dataTestId={`request-vendor-efs-check-button-${item().id}`}
                            >
                              <CurrencyUsdIcon
                                class={getEfsRequestStyling(item().id)}
                              />
                            </IconButton>
                          </TableCell>
                        </Show>
                        <TableCell
                          class={getRowBorderCellClass(index)}
                          style={cellStyle}
                        >
                          <Typography
                            variant="body1"
                            class={`${classes.totalAmount} font-normal!`}
                          >
                            {formatAmount(
                              Number(item().quantity) * Number(item().rate),
                            )}
                          </Typography>
                        </TableCell>

                        <TableCell
                          class={getRowBorderCellClass(index)}
                          style={{ ...cellStyle, 'min-width': 'fit-content' }}
                        >
                          <Show
                            when={
                              !isFalveyInsurance() ||
                              (isSuperAdmin() && item().adjustment)
                            }
                          >
                            <IconButton
                              onClick={() => {
                                setDeleteRow(index);
                                openDialogBox(
                                  DELETE_VENDOR_PAYMENTS_LINE_ITEM_DIALOG_ID,
                                );
                              }}
                              disabled={isLoadSureInsurance(index)}
                              dataTestId={`delete-vendor-line-item-button-${index}`}
                            >
                              <DeleteIcon class="text-[#B00020] cursor-pointer" />
                            </IconButton>
                          </Show>
                        </TableCell>
                      </TableRow>
                      <Show when={isUnqualifiedEFSCheckRequest()}>
                        <TableRowErrors
                          columnsLength={headers().length}
                          tableErrors={[
                            `Line item is in "${item().status}" state and cannot be paid via EFS`,
                          ]}
                          type="warning"
                          class="bg-white border-[rgb(245,157,37)]"
                        />
                      </Show>
                      <TableRowErrors
                        columnsLength={headers().length}
                        tableErrors={orderTableErrors(
                          ['rate', 'quantity', 'type'],
                          `vendors[${props.index}].lineItems[{index}].{field}`,
                          index,
                        )}
                      />
                    </Show>
                  );
                }}
              </Index>
            </TableBody>
          </Show>
          <TableFooter class={classes.tableFooter}>
            <TableRow class={classes.tableFooterRow}>
              <TableCell class="border-t! border-[#a8c5bf]!">
                <Show when={!isFalveyInsurance() || isSuperAdmin()}>
                  <IconButton
                    onClick={addRow}
                    dataTestId="add-vendor-line-item-button"
                  >
                    <PlusCircleOutlineIcon class={classes.addIcon} />
                  </IconButton>
                </Show>
              </TableCell>
              <TableCell
                class="border-t! border-[#a8c5bf]!"
                colSpan={isFalveyInsurance() ? 1 : 2}
              ></TableCell>
              <TableCell class="border-t! border-[#a8c5bf]!">
                <Typography variant="body1" class={classes.totalLabel}>
                  Total
                </Typography>
              </TableCell>
              <TableCell class="border-t! border-[#a8c5bf]!">
                <Typography variant="body1" class={classes.totalAmount}>
                  {formatAmount(computeTotalLineItems())}
                </Typography>
              </TableCell>
              <TableCell class="border-t! border-[#a8c5bf]!"></TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(
          DELETE_VENDOR_PAYMENTS_LINE_ITEM_DIALOG_ID,
        )}
        closeDialogBox={(_reason) => {
          setDeleteRow(null);
          closeDialogBox(DELETE_VENDOR_PAYMENTS_LINE_ITEM_DIALOG_ID);
        }}
        dataTestId={DELETE_VENDOR_PAYMENTS_LINE_ITEM_DIALOG_ID}
        id={DELETE_VENDOR_PAYMENTS_LINE_ITEM_DIALOG_ID}
        title={'Are you sure you want to delete this line item?'}
        onSubmit={() => {
          removeTableRow(deleteRow()!, props.vendor, 'vendor', 'lineItems');
          setDeleteRow(null);
        }}
        onSubmitText="Delete"
      />
    </>
  );
};
